import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, ArrowRight } from 'lucide-react';
import { useT } from '../i18n/LangContext';
import { useContent } from '../data/useContent';
import { PHONE, PHONE_HREF, EMAIL } from '../data/content';

export default function Footer() {
  const t = useT();
  const { SERVICES, AREAS } = useContent();

  const companyLinks = [
    ['/about', t['footer.aboutUs'], 'about'],
    ['/reviews', t['footer.reviews'], 'reviews'],
    ['/gallery', t['footer.gallery'], 'gallery'],
    ['/blog', t['footer.blog'], 'blog'],
    ['/contact', t['footer.contact'], 'contact'],
    ['/quote', t['footer.getQuote'], 'quote'],
  ];

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-coal">
      <div className="container-site py-20">
        <div className="flex flex-col items-start justify-between gap-8 border-b border-white/10 pb-16 md:flex-row md:items-end">
          <h2 className="max-w-xl font-serif text-4xl font-semibold leading-[1.05] sm:text-5xl">
            {t['footer.tagline1']} <span className="italic text-gold">{t['footer.tagline2']}</span>
          </h2>
          <Link data-testid="footer-quote-cta" to="/quote" className="gold-btn shrink-0">
            {t['footer.cta']} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid gap-12 py-14 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex flex-col leading-none">
              <span className="font-serif text-2xl font-bold tracking-wide">REVERENCE</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-gold">Services</span>
            </div>
            <address className="mt-5 space-y-1.5 text-sm not-italic text-silver">
              <p>Orléans, Ottawa, Ontario</p>
              <a data-testid="footer-phone" href={PHONE_HREF} className="block font-bold text-gold hover:text-gold-light">{PHONE}</a>
              <a data-testid="footer-email" href={`mailto:${EMAIL}`} className="block hover:text-white">{EMAIL}</a>
              <p className="pt-2 text-xs">{t['footer.hours']}<br />{t['footer.hours2']}</p>
            </address>
            <div className="mt-5 flex gap-3">
              {[['facebook', Facebook], ['instagram', Instagram], ['linkedin', Linkedin]].map(([name, Icon]) => (
                <a key={name} data-testid={`footer-social-${name}`} href="#" aria-label={name}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-silver transition-colors hover:border-gold hover:text-gold">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <nav aria-label="Footer services">
            <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-gold">{t['footer.services']}</h3>
            <ul className="space-y-2.5">
              {SERVICES.map((s) => (
                <li key={s.slug}><Link data-testid={`footer-service-${s.slug}`} to={`/${s.slug}`} className="text-sm text-silver transition-colors hover:text-gold">{s.name}</Link></li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Footer service areas">
            <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-gold">{t['footer.areas']}</h3>
            <ul className="space-y-2.5">
              {AREAS.map((a) => (
                <li key={a.slug}><Link data-testid={`footer-area-${a.slug}`} to={`/areas/${a.slug}`} className="text-sm text-silver transition-colors hover:text-gold">{a.name}</Link></li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Footer company">
            <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-gold">{t['footer.company']}</h3>
            <ul className="space-y-2.5">
              {companyLinks.map(([to, label, key]) => (
                <li key={to}><Link data-testid={`footer-link-${key}`} to={to} className="text-sm text-silver transition-colors hover:text-gold">{label}</Link></li>
              ))}
              <li><a data-testid="footer-sitemap-link" href="/sitemap.xml" className="text-sm text-silver transition-colors hover:text-gold">{t['footer.sitemap']}</a></li>
            </ul>
          </nav>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-8 text-xs text-silver/70 sm:flex-row">
          <p>{t['footer.rights']}</p>
          <p>{t['footer.trust']}</p>
        </div>
      </div>
    </footer>
  );
}
